import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddPage from './resolvers/resAddPage'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import AddRemoveTableSortable from 'components/AddRemoveTableSortable'
import { ReactReduxContext } from 'react-redux'
import { apiUrls } from 'consts'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'
import AddReport from 'pages/AddReport'
import AddFilter from 'pages/AddFilter'
import Entity from 'components/AddRemoveTableSortable/types/Entity'
import { setMessage } from 'redux/actions'
import Store from 'redux/Store'
import Editor from 'ckeditor5-custom-build/build/ckeditor'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import uploadAdapter from './utils/uploadAdapter'

const submitRef = React.createRef<HTMLButtonElement>()

const AddPage: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [initUrl, setInitUrl] = useState<string | false>(false)
    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})
    const [relationalFilters, setRelationalFilters] = useState<Array<Entity>>([])
    const [relationalReports, setRelationalReports] = useState<Array<Entity>>([])
    const [staticContent, setStaticContent] = useState<string>('')

    const { id } = useParams()
    const { store } = useContext(ReactReduxContext)

    const navigate = useNavigate()

    const [pageId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const {
      register,
      getValues,
      handleSubmit,
      reset,
      setValue,
      setFocus,
      setError,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddPage,
    })

    useInit({
      getValues,
      id: pageId,
      reset,
      setDataLoaded,
      setShowOverlay,
      setStaticContent,
      currentCustomer,
      setInitUrl,
    })

    useFocusError({
      errors,
      setFocus,
    })

    //this needs to be old-school function, not arrow function
    function uploadPlugin(editor) {
      editor.plugins._plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return uploadAdapter(loader)
      }
    }

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/pages' text='All Pages' />}
          actionInsert={
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Page
            </Button>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const commonParams = {
              submitForm,
              data,
              setShowOverlay,
              setError,
              id: pageId,
              name: 'Page',
              successNav: '/pages',
              extraData: {
                store,
                initUrl,
                lastUrl: store.getState().lastUrl.lastUrl,
                reports: relationalReports,
                filters: relationalFilters,
              },
            }

            return props.modalConfig !== undefined
              ? prepareSubmitFormFromModal({
                  ...commonParams,
                  modalConfig: props.modalConfig,
                })
              : prepareSubmitForm({
                  ...commonParams,
                  navigate: navigate,
                })
          })}
          id='main-form'
        >
          <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
            <Form.Control type='text' {...register('name')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='URL' name='url' errors={errors}>
            <Form.Control type='text' {...register('url')} />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <InputWrapper
            label='Static Content'
            name='static_content'
            errors={errors}
            colAttributes={{ md: 12 }}
          >
            <CKEditor
              editor={Editor}
              data={staticContent}
              config={{
                extraPlugins: [uploadPlugin],
              }}
              onChange={(event: any, editor: any) => {
                const data = editor.getData()
                setStaticContent(data)
                setValue('static_content', data)
              }}
            />
          </InputWrapper>

          <Button
            type='submit'
            name='save'
            ref={(ref as any) ?? submitRef}
            className='d-none'
          ></Button>
        </Form>

        {props.modalConfig === undefined && (
          <>
            <AddRemoveTableSortable
              getAllRowsUrl={apiUrls.pagesToReports + '?id=' + id}
              addRemoveRowUrl={apiUrls.pagesToReports}
              relationalFieldName='report_id'
              toggleActiveUrl={apiUrls.reports}
              maxTableHeight='600px'
              entityNameSingular='Report'
              entityNamePlural='Reports'
              sortRowsUrl={apiUrls.pagesToReports + '/' + apiUrls.actionSort}
              setShowOverlay={setShowOverlay}
              wrapperCssClasses='mb-5'
              editFormComponent={AddReport}
              onAddItem={
                id !== undefined
                  ? undefined
                  : (entity: Entity) => {
                      setRelationalReports((prevState) => [...prevState, entity])
                      return true
                    }
              }
              onRemoveItem={
                id !== undefined
                  ? undefined
                  : (entity: Entity) => {
                      setRelationalReports((prevState) =>
                        prevState.filter((r) => r.id !== entity.id),
                      )
                      return true
                    }
              }
            />

            <AddRemoveTableSortable
              getAllRowsUrl={apiUrls.filtersToPages + '?id=' + id}
              addRemoveRowUrl={apiUrls.filtersToPages}
              relationalFieldName='filter_id'
              toggleActiveUrl={apiUrls.filters}
              maxTableHeight='600px'
              entityNameSingular='Filter'
              entityNamePlural='Filters'
              sortRowsUrl={apiUrls.filtersToPages + '/' + apiUrls.actionSort}
              setShowOverlay={setShowOverlay}
              editFormComponent={AddFilter}
              onAddItem={
                id !== undefined
                  ? undefined
                  : (entity: Entity) => {
                      if (entity.parent_id !== null) {
                        const parentFilterAdded: Entity | undefined = relationalFilters.find(
                          (f) => f.id === entity.parent_id,
                        )
                        if (parentFilterAdded === undefined) {
                          Store.dispatch(
                            setMessage(
                              'Failed to create the object: Parent Filter must be included in the Page',
                              'error',
                              'instant',
                            ),
                          )
                          return false
                        }
                      }
                      setRelationalFilters((prevState) => [...prevState, entity])
                      return true
                    }
              }
              onRemoveItem={
                id !== undefined
                  ? undefined
                  : (entity: Entity) => {
                      if (entity.parent_id === null) {
                        const childFilterAdded: Entity | undefined = relationalFilters.find(
                          (f) => f.parent_id === entity.id,
                        )
                        if (childFilterAdded !== undefined) {
                          Store.dispatch(
                            setMessage(
                              'Failed to delete the object: Please, delete all Child Filters prior to the Parent Filter',
                              'error',
                              'instant',
                            ),
                          )
                          return false
                        }
                      }
                      setRelationalFilters((prevState) =>
                        prevState.filter((r) => r.id !== entity.id),
                      )
                      return true
                    }
              }
            />
          </>
        )}
      </>,
    )
  },
)

export default AddPage
